import { useState, useEffect } from 'react';
import axios from 'axios';
import { MapPin, X, Buildings } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Sprint B #4 — Modale de rattachement d'un poste DPGF à une pièce/plan.
 * Usage :
 *   <DPGFLocationPopover
 *      open={!!locationFor}
 *      onClose={() => setLocationFor(null)}
 *      poste={posteToEdit}
 *      chantierId={chantierId}
 *      onSave={(patch) => updatePoste(lotIdx, posteIdx, patch)}
 *   />
 */
export default function DPGFLocationPopover({ open, onClose, poste, chantierId, onSave }) {
  const [pieces, setPieces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [localTexte, setLocalTexte] = useState('');
  const [selectedPieceId, setSelectedPieceId] = useState(null);
  const [selectedPlanId, setSelectedPlanId] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLocalTexte(poste?.localisation_texte || '');
    setSelectedPieceId(poste?.piece_id || null);
    setSelectedPlanId(poste?.plan_id || null);
    setLoading(true);
    axios.get(`${API_URL}/api/chantiers/${chantierId}/dpgf/pieces-disponibles`, { withCredentials: true })
      .then((r) => setPieces(r.data?.items || []))
      .catch(() => toast.error('Erreur chargement pièces'))
      .finally(() => setLoading(false));
  }, [open, chantierId, poste]);

  const filtered = pieces.filter((p) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (p.piece_nom || '').toLowerCase().includes(q) || (p.plan_nom || '').toLowerCase().includes(q);
  });

  const handleSave = () => {
    onSave({
      plan_id: selectedPlanId || null,
      piece_id: selectedPieceId || null,
      localisation_texte: localTexte.trim() || null,
    });
    toast.success('Localisation mise à jour');
    onClose?.();
  };

  const handleClear = () => {
    onSave({ plan_id: null, piece_id: null, localisation_texte: null });
    toast.success('Localisation retirée');
    onClose?.();
  };

  const selectPiece = (item) => {
    setSelectedPieceId(item.piece_id);
    setSelectedPlanId(item.plan_id);
    setLocalTexte(`${item.piece_nom}${item.plan_nom ? ` (${item.plan_nom})` : ''}`);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-xl p-0" data-testid="dpgf-location-popover">
        <DialogHeader className="px-4 py-3 border-b border-[#E0E0DB] bg-[#FAFAF8]">
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'DM Serif Display, serif' }}>
            <MapPin size={20} className="text-[#C75B39]" weight="duotone" />
            Rattacher à une pièce ou un plan
          </DialogTitle>
        </DialogHeader>

        <div className="p-4 space-y-4">
          <div>
            <label className="text-xs font-semibold text-[#2C2C2C] uppercase tracking-wide block mb-1">
              Localisation libre <span className="text-[#9A9A9A] font-normal normal-case">(toujours possible)</span>
            </label>
            <Input
              value={localTexte}
              onChange={(e) => setLocalTexte(e.target.value)}
              placeholder="Ex : Cuisine RDC, SDB R+1, Façade nord…"
              className="rounded-none"
              data-testid="dpgf-location-text-input"
            />
          </div>

          <div className="border-t border-[#E0E0DB] pt-3">
            <label className="text-xs font-semibold text-[#2C2C2C] uppercase tracking-wide block mb-2">
              Ou sélectionner une pièce d'un plan
            </label>
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher une pièce…"
              className="rounded-none mb-2"
              data-testid="dpgf-location-search"
            />
            <div className="border border-[#E0E0DB] max-h-64 overflow-auto" data-testid="dpgf-location-list">
              {loading && <div className="p-4 text-center text-xs text-[#9A9A9A]">Chargement…</div>}
              {!loading && filtered.length === 0 && (
                <div className="p-4 text-center text-xs text-[#9A9A9A]" data-testid="dpgf-location-empty">
                  {pieces.length === 0 ? (
                    <>
                      <p>Aucune pièce dans les plans du chantier.</p>
                      <p className="mt-2 text-[#C75B39]">
                        Créez des pièces dans l'onglet <strong>Conception → Plans</strong> →
                      </p>
                    </>
                  ) : (
                    'Aucun résultat'
                  )}
                </div>
              )}
              {!loading && filtered.map((item) => {
                const isSel = selectedPieceId === item.piece_id;
                return (
                  <button
                    key={`${item.plan_id}-${item.piece_id}`}
                    onClick={() => selectPiece(item)}
                    className={`w-full text-left px-3 py-2 border-b border-[#F0F0EE] hover:bg-[#FFF5F2] flex items-center gap-2 ${isSel ? 'bg-[#FFF5F2] border-l-4 border-l-[#C75B39]' : ''}`}
                    data-testid={`dpgf-location-piece-${item.piece_id}`}
                  >
                    <Buildings size={14} className="text-[#9A9A9A] flex-shrink-0" />
                    <span className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-[#2C2C2C] truncate">{item.piece_nom}</div>
                      <div className="text-[10px] text-[#9A9A9A] truncate">
                        {item.plan_nom}{item.surface ? ` · ${item.surface} m²` : ''}{item.type_piece ? ` · ${item.type_piece}` : ''}
                      </div>
                    </span>
                    {isSel && <span className="text-[10px] text-[#C75B39] font-bold">✓ Sélectionnée</span>}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="border-t border-[#E0E0DB] bg-[#FAFAF8] px-4 py-2 flex items-center justify-between">
          <button
            onClick={handleClear}
            className="text-xs text-[#9A9A9A] hover:text-[#8B3A3A] flex items-center gap-1"
            data-testid="dpgf-location-clear"
          >
            <X size={12} /> Retirer la localisation
          </button>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="rounded-none" onClick={onClose}>Annuler</Button>
            <Button size="sm" className="rounded-none bg-[#C75B39] hover:bg-[#A14828] text-white" onClick={handleSave} data-testid="dpgf-location-save">
              Enregistrer
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
